"use server";

import { auth } from "@/auth";
import { canAccessDraft } from "@/lib/cat/draft-access";
import { retailLogCtmValidation } from "@/lib/cat/draft-activity-log";
import { persistCtmFromDraft } from "@/lib/cat/ctm/persist-from-draft";
import {
  buildNadinPurchaseOrderPayload,
  CtmPayloadBuildError,
  parseCtmTransmitResponse,
  transmitPurchaseOrder,
  validateCtmTransmitResponse,
} from "@/lib/cat/ctm-transmit";
import { getDraftLockHolder, releaseDraftLock } from "@/lib/cat/draft-lock";
import { resolveVehicleIdentificationSearch } from "@/lib/cat/vehicle-identification-resolve";
import { MulesoftRequestError } from "@/lib/mulesoft";
import { prisma } from "@/prisma";
import type { CtmTransmitResult } from "@/types/cat/ctm-transmit.types";

const CTM_TRANSMIT_ERROR_MESSAGE =
  "La transmission CTM a échoué. Réessayez ou contactez le support technique.";

export async function transmitRetailCtmAction({
  draftId,
}: {
  draftId: number;
}): Promise<CtmTransmitResult> {
  try {
    const session = await auth();
    if (!session?.user?.id) {
      return {
        success: false,
        code: "access_denied",
        message: "Non authentifié.",
      };
    }

    const draft = await prisma.draft.findUnique({
      where: { id: draftId },
      include: {
        tradeInVehicles: {
          orderBy: { sortOrder: "asc" },
          include: { brand: { select: { name: true } } },
        },
        bodyType: true,
      },
    });

    if (!draft) {
      return {
        success: false,
        code: "not_found",
        message: "Brouillon introuvable.",
      };
    }

    if (!canAccessDraft(session, draft)) {
      return {
        success: false,
        code: "access_denied",
        message: "Accès refusé à ce brouillon.",
      };
    }

    const lockHolder = await getDraftLockHolder(draftId);
    if (lockHolder && lockHolder.userId !== session.user.id) {
      return {
        success: false,
        code: "lock_denied",
        message: `Brouillon en cours d'édition par ${lockHolder.userName}.`,
      };
    }

    const brand = session.data?.kvps?.current?.parts?.brand?.trim();
    if (!brand) {
      return {
        success: false,
        code: "payload_error",
        message: "KVPS session invalide.",
      };
    }

    if (!draft.commissionYear || !draft.commissionNumber) {
      return {
        success: false,
        code: "payload_error",
        message: "Le brouillon ne contient pas d'identifiant commande valide.",
      };
    }

    const vehicleOutcome = await resolveVehicleIdentificationSearch({
      brand,
      commissionNumber: draft.commissionNumber,
      commissionYear: draft.commissionYear,
      vin: draft.vinNumber ?? undefined,
      bid: "995",
    });

    if (vehicleOutcome.kind === "error") {
      await retailLogCtmValidation({
        userId: session.user.id,
        kvps: draft.kvps,
        targetRef: `DRAFT-${draftId}`,
        result: "KO_TECH",
        metadata: "Recherche véhicule MuleSoft en échec",
      });
      return {
        success: false,
        code: "vehicle_lookup_error",
        message: vehicleOutcome.message,
      };
    }

    if (vehicleOutcome.kind !== "data") {
      await retailLogCtmValidation({
        userId: session.user.id,
        kvps: draft.kvps,
        targetRef: `DRAFT-${draftId}`,
        result: "KO_TECH",
        metadata: "Véhicule introuvable dans MuleSoft",
      });
      return {
        success: false,
        code: "vehicle_lookup_error",
        message: "Impossible de retrouver le véhicule dans MuleSoft.",
      };
    }

    const vehicleData = vehicleOutcome.data;
    if (vehicleData.isAlreadyAssigned) {
      await retailLogCtmValidation({
        userId: session.user.id,
        kvps: draft.kvps,
        targetRef: `DRAFT-${draftId}`,
        result: "KO_METIER",
        metadata: "Véhicule déjà contremarqué",
      });
      return {
        success: false,
        code: "vehicle_already_assigned",
        message: "Le véhicule est déjà contremarqué.",
      };
    }

    const saleType = draft.saleType?.trim();
    if (!saleType || saleType === "INDÉTERMINÉ") {
      await retailLogCtmValidation({
        userId: session.user.id,
        kvps: draft.kvps,
        targetRef: `DRAFT-${draftId}`,
        result: "KO_METIER",
        metadata: "Type de vente non renseigné ou indéterminé",
      });
      return {
        success: false,
        code: "payload_error",
        message:
          "Le type de vente n'est pas renseigné ou est indéterminé pour la transmission CTM.",
      };
    }

    let payload;
    try {
      payload = buildNadinPurchaseOrderPayload(draft);
    } catch (error) {
      if (error instanceof CtmPayloadBuildError) {
        await retailLogCtmValidation({
          userId: session.user.id,
          kvps: draft.kvps,
          targetRef: `DRAFT-${draftId}`,
          result: "KO_METIER",
          metadata: error.userMessage,
        });
        return {
          success: false,
          code: "payload_error",
          message: error.userMessage,
        };
      }
      throw error;
    }

    let response;
    try {
      response = await transmitPurchaseOrder(payload);
    } catch (error) {
      console.error("[transmitRetailCtmAction]", error);
      await retailLogCtmValidation({
        userId: session.user.id,
        kvps: draft.kvps,
        targetRef: `DRAFT-${draftId}`,
        result: "KO_TECH",
        metadata:
          error instanceof MulesoftRequestError
            ? "Erreur MuleSoft"
            : CTM_TRANSMIT_ERROR_MESSAGE,
      });
      return {
        success: false,
        code: "transmit_error",
        message: CTM_TRANSMIT_ERROR_MESSAGE,
      };
    }

    const validation = validateCtmTransmitResponse(response);
    if (!validation.valid) {
      await retailLogCtmValidation({
        userId: session.user.id,
        kvps: draft.kvps,
        targetRef: `DRAFT-${draftId}`,
        result: "KO_METIER",
        metadata: validation.message,
      });
      return {
        success: false,
        code: "business_error",
        message: validation.message,
      };
    }

    const parsed = parseCtmTransmitResponse(response);
    const commissionRef =
      parsed.commissionRef ??
      `${draft.commissionNumber}/${draft.commissionYear}`;

    await prisma.$transaction(async (tx) => {
      await persistCtmFromDraft(tx, { draft, vehicleData, brand });
      await tx.draft.delete({ where: { id: draftId } });
    });
    await releaseDraftLock(draftId, session.user.id);

    await retailLogCtmValidation({
      userId: session.user.id,
      kvps: draft.kvps,
      targetRef: commissionRef,
      result: "OK",
      metadata: "Transmission CTM réussie",
    });

    return {
      success: true,
      response,
    };
  } catch (error) {
    console.error("[transmitRetailCtmAction]", error);
    return {
      success: false,
      code: "transmit_error",
      message: CTM_TRANSMIT_ERROR_MESSAGE,
    };
  }
}
